import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge } from '@/components/StatusBadge'
import { DangerDialog } from '@/components/DangerDialog'
import { useToast } from '@/providers/UiProviders'
import { fetchBotsForMod, deleteBot, BOT_MOD_STR as S, type BotModRow } from './moderationApi'

/**
 * A5.13 — Botni o'chirish (hard-delete). LIST = REAL GET /admin/bots. O'chirish =
 * DELETE /admin/bots/:id (lane-A greenfield — deploy'gacha 404 → xatolik toast'i).
 * DangerDialog L3: sabab + nomni terish shart (qaytarilmaydigan amal).
 */
export function DeleteBotScreen() {
  const { toast } = useToast()
  const [target, setTarget] = useState<BotModRow | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  const botLabel = (r: BotModRow) => (r.username ? `@${r.username}` : r.first_name || `#${r.id}`)

  const columns = useMemo<AdminColumn<BotModRow>[]>(
    () => [
      {
        key: 'username',
        header: S.bot,
        type: 'text',
        minWidth: 180,
        render: (r) => (
          <Link to={`/bots/${r.id}`} className="text-accent hover:underline">
            {botLabel(r)}
          </Link>
        ),
      },
      { key: 'first_name', header: S.name, type: 'text', minWidth: 160 },
      {
        key: 'revoked',
        header: S.status,
        type: 'text',
        width: 140,
        render: (r) =>
          r.revoked ? (
            <StatusBadge status="banned" label={S.restricted} />
          ) : (
            <StatusBadge status="active" label={S.active} />
          ),
      },
      { key: 'created_at', header: S.registered, type: 'date', width: 150 },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<BotModRow>[]>(
    () => [
      {
        id: 'delete',
        label: S.deleteBot,
        icon: <Trash2 size={13} />,
        danger: true,
        // Official/system bots (Core, Grom, Verify, …) are permanent — they stay
        // editable (name/photo/description) but can never be deleted.
        onSelect: (r) => {
          if (r.is_official) {
            toast(S.officialCantDelete, { tone: 'error' })
            return
          }
          setTarget(r)
        },
      },
    ],
    [],
  )

  const fetcher = useCallback(fetchBotsForMod, [])

  return (
    <>
      <PageHeader title={S.deleteTitle} screenId="A5.13" />
      <DataTable<BotModRow>
        key={reloadKey}
        screenId="A5.13"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <Trash2 size={28} strokeWidth={1.5} />, title: S.empty }}
      />

      {target && (
        <DangerDialog
          open
          onClose={() => setTarget(null)}
          screenId="A5.13"
          level={3}
          title={S.deleteConfirmTitle}
          description={S.deleteConfirmDesc}
          confirmLabel={S.deleteBot}
          objectName={target.username ? `@${target.username}` : `#${target.id}`}
          onConfirm={async () => {
            try {
              await deleteBot(target.id)
              toast(S.deleted, { tone: 'success' })
              setTarget(null)
              setReloadKey((k) => k + 1)
            } catch (e) {
              toast(S.failed, {
                tone: 'error',
                ...(e instanceof Error && e.message ? { description: e.message } : {}),
              })
              throw e
            }
          }}
        />
      )}
    </>
  )
}
